import {Component, HostListener, OnInit} from '@angular/core';
import {TranslocoService} from '@ngneat/transloco';
import {FileData, FileErrors} from '../../../model/import-csv/file-data';
import {CsvErrorList} from '../../../model/import-csv/csv-error';
import {TriggerCsvErrors} from './trigger-csv-errors';
import {TriggerPropertiesErrors} from './trigger-properties-errors';
import {CommonMessagesService} from '../../../services/common-messages.service';
import {Tenant} from '../../../model/tenant';
import {Organization} from '../../../model/organization';
import {TenantsService} from '../../../services/tenants.service';
import {AuthGuard} from '../../../common/auth-guard.service';
import {LoggedUser} from '../../../common/logged-user.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {TriggerPersistRequestCsv} from '../../../model/import-csv/trigger-persist-request-csv';
import {CsvTriggerData} from '../../../model/import-csv/csv-trigger-data';
import {TriggersService} from '../../../services/triggers.service';
import {AppConfig} from '../../../shared/global-variables';
import {HttpErrorResponse} from '@angular/common/http';
import {MessageService} from 'primeng/api';

@Component({
  selector: 'app-triggers-csv',
  templateUrl: './triggers-csv.component.html',
  styleUrls: ['./triggers-csv.component.scss']
})
export class TriggersCsvComponent implements OnInit {

  public triggerPropertiesErrors: TriggerPropertiesErrors = new TriggerPropertiesErrors(this.i18n);

  public fileFormatIncorrect: boolean = false;
  public infoCollapsed: boolean = true;
  public files: FileData[] = [];
  public filesWithoutErrors: File[] = [];
  public triggers: CsvTriggerData[] = [];

  public loadedData: boolean = false;
  public filesLoaded: boolean = false;
  public filesUploaded: boolean = false;

  public uploadingTriggers: boolean = false;

  public rows: number = 10;
  public first: number = 0;
  public totalTriggers: number;

  tenantSelected: boolean;
  organizationSelected: boolean;
  tenants: Tenant[];
  organizations: Organization[];
  tenant: Tenant;
  organization: Organization;
  csvTriggerForm: FormGroup;

  constructor(
    private readonly commonMessageService: CommonMessagesService,
    private readonly triggersService: TriggersService,
    private readonly i18n: TranslocoService,
    private readonly messageService: MessageService,
    private tenantsService: TenantsService,
    private authGuard: AuthGuard,
    private loggedUser: LoggedUser,
    private formBuilder: FormBuilder
  ) {
  }

  ngOnInit(): void {
    this.loadData();
  }

  private loadData(): void {
    this.loadedData = true;

    const tenant: { name: string, uuid: string } = this.tenant ? {
      name: this.tenant.name,
      uuid: this.tenant.uuid
    } : null;

    const organization: { name: string, uuid: string } = this.organization ? {
      name: this.organization.name,
      uuid: this.organization.uuid
    } : null;

    this.csvTriggerForm = this.formBuilder.group(
      {
        tenant: [tenant, Validators.required],
        organization: [{value: organization, disabled: true}]
      }
    );
  }

  public async processFiles(files: File[]): Promise<void> {
    const filePromises: Promise<FileData>[] = files.map((file: File, index: number) => {
      const fileIdentifier: string = 'id-' + index + '-' + (new Date()).getTime();
      return this.readFile(fileIdentifier, file);
    });

    const _files: FileData[] = await Promise.all(filePromises);

    this.files = this.files.concat(_files);
    this.filesWithoutErrors = this.files
      .filter(file => file.fileErrors == null)
      .map(file => file.file);
    this.filesLoaded = true;
    this.filesUploaded = false;
    this.infoCollapsed = true;

    if (this.authGuard.superAdminRequired()) {
      this.tenantsService.getTenantSummaries().toPromise().then(response => {
        this.tenants = response.tenants;
      });
    } else {
      this.tenants = this.loggedUser.userTenants;
      this.tenant = this.tenants[0];
      this.csvTriggerForm.get('tenant').setValue(this.tenant);
      this.csvTriggerForm.get('tenant').disable();
      this.loadOrganizations(this.tenant.uuid).then((): void => {
        if (this.loggedUser.user.organizationUuid !== undefined) {
          this.organization = this.loggedUser.userOrganizations[0];
          this.csvTriggerForm.get('organization').setValue(this.organization);
          this.organizationSelected = true;
        } else {
          this.enableTenantDependentElements();
        }
      });
    }
  }

  private async loadOrganizations(tenantUuid: string): Promise<any> {
    this.disableTenantDependentElements();
    const response = await Promise.all([
      this.tenantsService.getTenantOrganizationSummaries(tenantUuid).toPromise(),
    ]);
    this.organizations = response[0].organizations;
  }

  private enableTenantDependentElements(): void {
    this.tenantSelected = true;
    this.csvTriggerForm.get('organization').enable();
  }

  private disableTenantDependentElements(): void {
    this.tenantSelected = false;
    this.csvTriggerForm.get('organization').disable();
  }

  onTenantChange(event: any): void {
    if (event?.value) {
      this.tenantSelected = true;
      this.organizationSelected = false;
      this.csvTriggerForm.get('organization').setValue(null);
      this.loadOrganizations(event.value.uuid).then((): void => {
        this.enableTenantDependentElements();
      });
    }
  }

  public onOrganizationChange(event: any): void {
    if (event?.value) {
      this.organizationSelected = true;
    }
  }

  public onClearOrganization(): void {
    this.csvTriggerForm.get('organization').setValue(undefined);
  }

  private readFile(fileIdentifier: string, file: File): Promise<FileData> {
    return new Promise((resolve, reject): void => {
      const reader: FileReader = new FileReader();
      reader.onload = async (): Promise<void> => {
        try {
          const fileReader: string[] = (reader.result as string).split('\n');
          const fileErrors: FileErrors = this.processTriggerFiles(fileIdentifier, fileReader);
          const fileData: FileData = {
            fileIdentifier,
            file,
            fileErrors
          };
          resolve(fileData);
        } catch (err) {
          reject(err);
        }
      };
      reader.readAsText(file, 'ISO-8859-3');
      reader.onerror = (error: ProgressEvent<FileReader>): void => {
        reject(error);
      };
    });
  }

  public deleteAttachment(index: number): void {
    const fileIdentifier: string = this.files[index]['fileIdentifier'];
    this.files.splice(index, 1);
    if (this.files.length == 0) {
      this.filesLoaded = false;
      this.infoCollapsed = false;
    }
    this.triggers = this.triggers.filter(trigger => trigger.fileIdentifier != fileIdentifier);
    this.totalTriggers = this.triggers.length;
    this.filesWithoutErrors = this.files
      .filter(file => file.fileErrors == null)
      .map(file => file.file);
  }

  private processTriggerFiles(fileIdentifier: string, file: string[]): FileErrors | null {
    let triggers: CsvTriggerData[] = [];
    let fileErrors: FileErrors = {
      lines: [],
    };

    file.forEach((line: string, lineIndex: number): void => {
      line = line.replace(/[\n\r\t\v\f]/gm, '');
      if (line.trim() !== '' && !line.toLowerCase().includes('entityidentifier')) {
        const rowData: string[] = line.split(';');
        if (this.validateNumberOfParams(rowData)) {
          const trigger: TriggerPersistRequestCsv = this.getTriggerPersistRequestCsv(rowData);
          let csvTriggerData: CsvTriggerData = {
            trigger,
            fileIdentifier,
            status: 'NONE'
          };

          const errors: CsvErrorList = this.validateTriggerData(csvTriggerData);
          if (errors != null) {
            csvTriggerData.status = 'ERROR';
            csvTriggerData.errors = errors;
            fileErrors.lines.push(lineIndex);
          } else {
            csvTriggerData.status = 'CORRECT';
          }

          triggers.push(csvTriggerData);
        } else {
          fileErrors.lines.push(lineIndex);
        }
      }
    });
    this.fileFormatIncorrect = false;

    if (triggers.length > 0) {
      this.triggers = this.triggers.concat(triggers);
    }
    this.totalTriggers = this.triggers.length;
    return fileErrors.lines.length > 0 ? fileErrors : null;
  }

  private validateNumberOfParams(rowData: string[]): boolean {
    return rowData.length >= 3;
  }

  private getTriggerPersistRequestCsv(rowData: string[]): TriggerPersistRequestCsv {
    const [name, entityType, entityIdentifier, sensorName, operation, value, triggerType, triggerCategory, triggerdisabledRepeatedEvents] = rowData;

    return {
      name,
      entityType,
      entityIdentifier,
      sensorName,
      operation,
      value,
      triggerType: typeof triggerType !== 'undefined' && 'USER' === triggerType.toUpperCase() ? 'USER' : 'SYSTEM',
      triggerCategory: typeof triggerCategory !== 'undefined' && 'WARNING' === triggerCategory.toUpperCase() ? 'WARNING' : 'ALARM',
      triggerdisabledRepeatedEvents: (typeof triggerCategory !== 'undefined' && 'TRUE' === triggerdisabledRepeatedEvents.toUpperCase())
    };
  }

  private validateTriggerData(csvTriggerData: CsvTriggerData): CsvErrorList | null {
    const trigger: TriggerPersistRequestCsv = csvTriggerData.trigger;
    const csvTriggerErrors: TriggerCsvErrors = new TriggerCsvErrors(this.i18n);

    let present: boolean = csvTriggerErrors.required(trigger, 'name');
    if (present) {
      csvTriggerErrors.noSpecialCharacters(trigger, 'name');
      csvTriggerErrors.noWhitespaces(trigger, 'name');
      csvTriggerErrors.maxLength(trigger, 'name', 255);
    }

    present = csvTriggerErrors.required(trigger, 'entityType');
    if (present) {
      csvTriggerErrors.isDeviceOrAsset(trigger, 'entityType');
    }

    present = csvTriggerErrors.required(trigger, 'entityIdentifier');
    if (present) {
      csvTriggerErrors.noSpecialCharacters(trigger, 'entityIdentifier');
      csvTriggerErrors.noWhitespaces(trigger, 'entityIdentifier');
      csvTriggerErrors.minLength(trigger, 'entityIdentifier', AppConfig.FORM_IDENTIFIER_MIN_LENGTH);
      csvTriggerErrors.maxLength(trigger, 'entityIdentifier', 255);
    }

    present = csvTriggerErrors.required(trigger, 'sensorName');
    if (present) {
      csvTriggerErrors.noSpecialCharacters(trigger, 'sensorName');
      csvTriggerErrors.maxLength(trigger, 'sensorName', 255);
    }

    present = csvTriggerErrors.required(trigger, 'operation');
    if (present) {
      csvTriggerErrors.validOperation(trigger, 'operation');
    }

    present = csvTriggerErrors.required(trigger, 'value');
    if (present) {
      csvTriggerErrors.maxLength(trigger, 'value', 255);
      csvTriggerErrors.validValue(trigger, 'value');
    }

    // If trigger not present "SYSTEM"

    return Object.entries(csvTriggerErrors.errors).length > 0 ? csvTriggerErrors.errors : null;
  }

  confirmImport(): void {
    if (this.csvTriggerForm.invalid) {
      return;
    }
    if (this.files.length > this.filesWithoutErrors.length) {
      this.commonMessageService.showImportCsvConfirmation(() => this.importCsvFiles());
    } else {
      this.importCsvFiles();
    }
  }

  public importCsvFiles(): void {
    this.uploadingTriggers = true;

    const formValue = this.csvTriggerForm.getRawValue();
    const tenantUuid = formValue.tenant.uuid;
    const organizationUuid = formValue.organization?.uuid;

    this.triggersService.importTriggersFromCsv(this.filesWithoutErrors, tenantUuid, organizationUuid).subscribe(
      (): void => {
        this.commonMessageService.showSuccessfullyUploadedMessage();
      }, (httpErrorResponse: HttpErrorResponse): void => {
        if (httpErrorResponse.status === 409) {
          this.showConflictMessage(httpErrorResponse.error?.errorMessage);
        }
      },
      (): void => {
        this.uploadingTriggers = false;
        this.filesUploaded = true;
        this.files = [];
        this.filesWithoutErrors = [];
        this.triggers = [];
      });
  }

  private showConflictMessage(errorMessage: string): void {
    const summary = this.i18n.translate('error.summary.importCsv');
    let detail = this.i18n.translate(errorMessage);
    if (detail === errorMessage) {
      detail = this.i18n.translate('error.summary.unexpected');
    }
    this.messageService.add({severity: 'error', summary, detail, sticky: true});
    this.uploadingTriggers = false;
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return !this.files || this.files.length === 0;
  }
}
